class AlgoliaSortBy extends HTMLElement {
  constructor() {
    super();

    this.connectedCallback = this.connectedCallback.bind(this);
    this.createWidget = this.createWidget.bind(this);
    this.render = this.render.bind(this);
    this.renderContainer = this.renderContainer.bind(this);
    this.renderDesktop = this.renderDesktop.bind(this);
    this.renderDesktopContainer = this.renderDesktopContainer.bind(this);
    this.renderDesktopContent = this.renderDesktopContent.bind(this);
    this.renderDesktopWrapper = this.renderDesktopWrapper.bind(this);
    this.renderMobile = this.renderMobile.bind(this);
    this.renderMobileContainer = this.renderMobileContainer.bind(this);
    this.renderMobileContent = this.renderMobileContent.bind(this);
    this.renderMobileWrapper = this.renderMobileWrapper.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      sortByLabel: this.getAttribute('sort-by-label')
    }
  }

  /**
   * Creates an instance of the sort by widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @returns {Object} The created sort by widget instance.
   */
  createWidget(configuration, instances) {
    const { connectSortBy } = instances.instantSearch.connectors;
    const connectedSortBy = connectSortBy((renderOptions, isFirstRender) => this.render(configuration, isFirstRender, renderOptions));

    const selector = `.${configuration.container}`;
    const container = document.querySelector(selector);

    const items = instances.algoliaShopify.sortOrders;

    const sortByWidget = connectedSortBy({ container, items });
    return sortByWidget;
  }

  /**
   * Renders the sort by widget with the provided render options.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {boolean} isFirstRender - Indicates whether this is the first render.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @returns {void}
   */
  render(configuration, isFirstRender, renderOptions) {
    configuration.isMobile ? this.renderMobile(renderOptions, isFirstRender) : this.renderDesktop(renderOptions, isFirstRender);
  }

  /**
   * Renders the container for the sort by widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered container.
   */
  renderContainer(configuration, html) {
    return configuration.isMobile ? this.renderMobileContainer(configuration, html) : this.renderDesktopContainer(configuration, html);
  }

  /**
   * Renders the sort by widget for desktop with the provided render options.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @param {boolean} isFirstRender - Indicates whether this is the first render.
   * @returns {void}
   */
  renderDesktop(renderOptions, isFirstRender) {
    const { canRefine, currentRefinement, options, refine, widgetParams } = renderOptions;
    const { container } = widgetParams;

    container.classList.toggle('hidden', !canRefine);

    if (isFirstRender) {
      const elements = this.renderDesktopWrapper();

      elements.forEach(element => container.appendChild(element));

      const select = container.querySelector('select');
      if (select) {
        select.addEventListener('change', event => refine(event.target.value));
        options.map(option => {
          const optionEl = this.renderDesktopContent(currentRefinement, option);
          select.appendChild(optionEl);
        });
      }
    } else {
      const select = container.querySelector('select');
      if (select) {
        const selectedOption = select.options[select.selectedIndex];
        const sortIndex = selectedOption.value;
        if(currentRefinement !== sortIndex) {
          refine(sortIndex)
        }
      }
    }
  }

  /**
   * Renders the container for the desktop sort by widget.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered desktop container.
   */
  renderDesktopContainer(configuration, html) {
    return html`
      <div class="facet-filters sorting caption">
        <div class="${configuration.container} ais-sort facet-filters__field"></div>
      </div>
    `;
  }

  /**
   * Renders the desktop content.
   * @param {string} currentRefinement - The current refinement value.
   * @param {Object} option - The option to render.
   * @returns {string} The rendered desktop content HTML.
   */
  renderDesktopContent(currentRefinement, option) {
    const isSelected = option.value === currentRefinement;
    const optionEl = document.createElement('option');
    optionEl.value = option.value;
    optionEl.textContent = option.label
    optionEl.selected = isSelected ? "selected" : "";
    return optionEl;
  }

  /**
   * Renders the desktop wrapper.
   * @returns {string} The rendered desktop wrapper HTML.
   */
  renderDesktopWrapper() {
    const iconCaretDown = document.querySelector('algolia-icon-caret-down');
    const iconCaretDownContent = iconCaretDown ? iconCaretDown.render() : ['', ''];

    const label = document.createElement('label');
    label.classList.add('facet-filters__label', 'p-bold');
    label.htmlFor = 'SortBy';
    label.textContent = this.settings.sortByLabel;

    const div = document.createElement('div');
    div.classList.add('select');

    const select = document.createElement('select');
    select.classList.add('facet-filters__sort', 'select__select', 'caption-large');
    select.id = 'SortBy';
    select.name = 'sort_by';

    div.appendChild(select);
    iconCaretDownContent.forEach(icon =>
      div.appendChild(icon)
    );

    return [label, div];
  }

  /**
   * Renders the sort by widget for mobile with the provided render options.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @param {boolean} isFirstRender - Indicates whether this is the first render.
   * @returns {void}
   */
  renderMobile(renderOptions, isFirstRender) {
    const { canRefine, currentRefinement, options, refine, widgetParams } = renderOptions;
    const { container } = widgetParams;

    container.classList.toggle('hidden', !canRefine);

    if (isFirstRender) {
      const elements = this.renderMobileWrapper();
      elements.forEach(element => container.appendChild(element));

      const fieldset = container.querySelector('fieldset');
      if (fieldset) {
        fieldset.addEventListener('change', event => refine(event.target.value));
        options.forEach(option => {
          const optionEl = this.renderMobileContent(currentRefinement, option);
          fieldset.appendChild(optionEl);
        });
      }
    }
  }

  /**
   * Renders the container for the mobile sort by widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered mobile container.
   */
  renderMobileContainer(configuration, html) {
    return html`<div class="${configuration.container} mobile-facets__sort-wrapper"></div>`;
  }

  /**
   * Renders the mobile content.
   * @param {string} currentRefinement - The current refinement value.
   * @param {Object} option - The option to render.
   * @returns {string} The rendered mobile content HTML.
   */
  renderMobileContent(currentRefinement, option) {
    const isSelected = option.value === currentRefinement;
    const iconRadio = this.querySelector('algolia-icon-radio');
    const iconRadioContent = iconRadio ? iconRadio.render() : [];

    const div = document.createElement('div');
    div.classList.add('mobile-facets__sort-radio-option-wrapper');

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'sort_by';
    input.value = option.value;
    input.checked = isSelected ? "checked" : "";

    const label = document.createElement('label');
    label.textContent = option.label;

    div.appendChild(input);
    iconRadioContent.forEach(element => div.appendChild(element));
    div.appendChild(label);

    return div;
  }

  /**
   * Renders the mobile wrapper.
   * @returns {string} The rendered mobile wrapper HTML.
   */
  renderMobileWrapper() {
    const legend = document.createElement('legend');
    legend.classList.add('mobile-facets__sort-header', 'p-regular', 'p-bold');
    legend.htmlFor = 'SortBy-mobile';
    legend.textContent = this.settings.sortByLabel;

    const fieldset = document.createElement('fieldset');
    fieldset.classList.add('mobile-facets__sort');

    return [legend, fieldset];
  }
}

customElements.define("algolia-sort-by", AlgoliaSortBy);